import { memo } from 'react'
import { textVariant } from '@/config'
import { MessageStatus } from './MessageStatus'
import { createStyles } from '@codeleap/styles'
import { useConditionalMessageStyle } from '../hooks'
import { useMessageContext } from 'stream-chat-react'
import { MessageProps } from '../types'
import { PropsOf } from '@emotion/react'
import { View } from '@/components'
import { isThreadParentMessage } from '@packages/chat'
import { Chat } from '@/config/packages'

export type MessageFooterProps = MessageProps & PropsOf<typeof View>

const MessageFooterWithContext = (props: MessageFooterProps) => {
  const { message, threadList } = props
  const isMyMessage = props.isMyMessage()
  const isThreadParent = isThreadParentMessage(message, threadList)
  const { selectModeActive } = Chat.Store.useSelectMode({ threadList })
  const { styles } = useConditionalMessageStyle(baseStyle, myMessageStyle)

  const threadHeadSelectOffset = selectModeActive && isThreadParent
    ? styles.threadHeadSelectOffset
    : null

  return (
    <View style={[styles.wrapper, props.style, threadHeadSelectOffset]}>
      {!isMyMessage && !isThreadParent && <div style={{ opacity: 0 }}><MessageStatus /></div>}
      <p style={styles.createdAt}>{formatTime(message?.created_at)}</p>
      <MessageStatus />
    </View>
  )
}

const MemoizedFooter = memo(
  MessageFooterWithContext,
  (prevProps: MessageFooterProps, nextProps: MessageFooterProps) => {
    return (
      prevProps.message.created_at === nextProps.message.created_at
    )
  },
)

export const MessageFooter = (props: MessageFooterProps) => {
  const messageProps = useMessageContext()
  return <MemoizedFooter {...messageProps} {...props} />
}

function formatTime(timestamp: string | Date) {
  const date = new Date(timestamp)
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  })
}

const baseStyle = createStyles(theme => ({
  createdAt: {
    ...textVariant('c3'),
    color: theme.colors.textCaption,
  },
  wrapper: {
    marginTop: theme.spacing.value(0.5),
    paddingLeft: theme.spacing.value(2),
    ...theme.presets.row,
    ...theme.presets.alignEnd,
    marginLeft: theme.size.large + theme.spacing.value(1),
    marginBottom: theme.spacing.value(0.5),
  },
  threadHeadSelectOffset: {
    marginLeft: theme.spacing.value(6),
  },
}))

const myMessageStyle = createStyles(theme => ({
  createdAt: {
    marginLeft: 'auto',
  },
  wrapper: {
    paddingRight: theme.spacing.value(2),
    marginLeft: 'unset',
  },
}))
